'use client';

import { t } from 'mobx-state-tree';

import type { UpdaterState } from '@/app/sdk';

const updaterPhases = ['idle', 'checking', 'available', 'downloading', 'readyToInstall', 'error'] as const;
const updaterTriggers = ['auto', 'manual'] as const;

export const UpdaterStoreModel = t
  .model('UpdaterStore', {
    phase: t.optional(t.enumeration('UpdaterPhase', updaterPhases), 'idle'),
    trigger: t.optional(t.enumeration('UpdaterTrigger', updaterTriggers), 'auto'),
    currentVersion: t.optional(t.string, ''),
    targetVersion: t.maybeNull(t.string),
    releaseDate: t.maybeNull(t.string),
    releaseName: t.maybeNull(t.string),
    releaseNotes: t.maybeNull(t.string),
    releaseUrl: t.maybeNull(t.string),
    progressPercent: t.maybeNull(t.number),
    transferredBytes: t.maybeNull(t.number),
    totalBytes: t.maybeNull(t.number),
    errorMessage: t.maybeNull(t.string),
    checkedAt: t.maybeNull(t.number),
    updatedAt: t.optional(t.number, 0),
    phaseStartedAt: t.optional(t.number, 0),
    elapsedSeconds: t.optional(t.number, 0),
    downloadRequestedAt: t.maybeNull(t.number),
    retryAttempt: t.optional(t.number, 0),
    /** Dev-only: when true, live SDK updater IPC is ignored for UI state. */
    isMockEnabled: t.optional(t.boolean, false),
    /** Dev-only: last mock scenario label or id for debugging. */
    activeMockScenarioId: t.maybeNull(t.string),
  })
  .views((self) => ({
    get showUpdaterPill() {
      return self.phase === 'available'
        || self.phase === 'downloading'
        || self.phase === 'readyToInstall'
        || self.phase === 'error';
    },
    get updaterState(): UpdaterState {
      return {
        phase: self.phase,
        trigger: self.trigger,
        currentVersion: self.currentVersion,
        targetVersion: self.targetVersion,
        releaseDate: self.releaseDate,
        releaseName: self.releaseName,
        releaseNotes: self.releaseNotes,
        releaseUrl: self.releaseUrl,
        progressPercent: self.progressPercent,
        transferredBytes: self.transferredBytes,
        totalBytes: self.totalBytes,
        errorMessage: self.errorMessage,
        checkedAt: self.checkedAt,
        updatedAt: self.updatedAt,
        phaseStartedAt: self.phaseStartedAt,
        elapsedSeconds: self.elapsedSeconds,
        downloadRequestedAt: self.downloadRequestedAt,
        retryAttempt: self.retryAttempt,
      };
    },
  }))
  .actions((self) => ({
    setUpdaterState(state: UpdaterState) {
      self.phase = state.phase;
      self.trigger = state.trigger;
      self.currentVersion = state.currentVersion;
      self.targetVersion = state.targetVersion;
      self.releaseDate = state.releaseDate;
      self.releaseName = state.releaseName;
      self.releaseNotes = state.releaseNotes;
      self.releaseUrl = state.releaseUrl;
      self.progressPercent = state.progressPercent;
      self.transferredBytes = state.transferredBytes;
      self.totalBytes = state.totalBytes;
      self.errorMessage = state.errorMessage;
      self.checkedAt = state.checkedAt;
      self.updatedAt = state.updatedAt;
      self.phaseStartedAt = state.phaseStartedAt;
      self.elapsedSeconds = state.elapsedSeconds;
      self.downloadRequestedAt = state.downloadRequestedAt;
      self.retryAttempt = state.retryAttempt;
    },
    setUpdaterMockEnabled(enabled: boolean) {
      self.isMockEnabled = enabled;
      if (!enabled) {
        self.activeMockScenarioId = null;
      }
    },
    setActiveMockScenarioId(id: string | null) {
      self.activeMockScenarioId = id;
    },
  }));
